import { useEffect, useRef } from 'react';
import type { Editor } from '@tiptap/react';
import { useWorkspaceService } from '@/lib/workspace/use-workspace-service';
import { buildDocumentPreviewHtml } from '@/lib/documents/preview/build-preview-markup';

const DEBOUNCE_MS = 1500;

interface UsePreviewMarkupOptions {
  /** Entity id of the document whose card preview this updates. */
  documentId: string;
  /** Gate — only emit when the user can edit (false for read-only viewers). */
  enabled: boolean;
}

/**
 * Keeps a document's card preview markup in sync with its content. On each
 * editor change (debounced 1.5s), serialize the doc to HTML, sanitize + cap it
 * via {@link buildDocumentPreviewHtml}, and ship it to the server through the
 * workspace service. This is what renders the live snippet on document cards.
 *
 * Engine-neutral port of the legacy Lexical `PreviewMarkupPlugin`: the only
 * engine-specific call, `$generateHtmlFromNodes(editor)`, becomes Tiptap's
 * `editor.getHTML()`. Everything else (debounce, last-sent dedup, edit gate) is
 * identical.
 */
export function usePreviewMarkup(
  editor: Editor | null,
  { documentId, enabled }: UsePreviewMarkupOptions,
): void {
  const service = useWorkspaceService();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Last preview we sent for this doc. The update listener also fires during
  // initial Yjs hydration; comparing against the last-sent markup avoids
  // re-emitting an identical preview just because the user opened the doc.
  const lastSentRef = useRef<string | null>(null);

  useEffect(() => {
    if (!editor || !enabled) return;

    const onUpdate = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const { markup, kind } = buildDocumentPreviewHtml(editor.getHTML());
        if (lastSentRef.current === markup) return;
        lastSentRef.current = markup;
        service.updatePreviewMarkup(documentId, markup, kind);
      }, DEBOUNCE_MS);
    };

    editor.on('update', onUpdate);
    return () => {
      editor.off('update', onUpdate);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [editor, enabled, documentId, service]);
}
